import BaseEnumService from "@edaz/services/baseEnumService";
import { observable } from "mobx";
import type EnumRepository from "repositories/enumRepository";
import type { ExternalEnums, SimpleListEnums } from "../config";
import { SystemEnums } from "../config";
import type ChargeType from "../entities/chargeType";
import type CountryResponse from "../entities/countryResponse";
import type Enum from "../entities/enum";
import type FuelTypeResponse from "entities/fuelTypeResponse";
import pluralize from "pluralize";
import { getEnumAttribute, getEnumAttributes } from "../utils/enumerable";
import type EventBus from "./eventBus";
import camelCase from "lodash/camelCase";
import type ExemptionReasonResponse from "../entities/exemptionReasonResponse";
import type LocaleProvider from "./localeProvider";
import type PaymentTemplateSimpleListResponse from "entities/paymentTemplateSimpleListResponse";
import type DistributorSimpleListResponse from "entities/distributorSimpleListResponse";
import type MunicipalitySimpleListResponse from "entities/municipalitySimpleListResponse";
import type RegionSimpleListResponse from "entities/regionSimpleListResponse";
import type RoadsSimpleListResponse from "entities/roadsSimpleListResponse";
import type NotifiableExemptionReasonSimpleList from "entities/notifiableExemptionReasonSimpleList";
import type InventoryAccountsResponse from "entities/InventoryAccountsResponse";

export type SystemEnumTypes = (typeof SystemEnums)[number];
export type ExternalEnumTypes = (typeof ExternalEnums)[number];
export type SimpleListEnumTypes = (typeof SimpleListEnums)[number];
export type AllEnums = ExternalEnumTypes | SystemEnumTypes | SimpleListEnumTypes;

function IsObject(x: any) {
  return typeof x === "object" ? x !== null : typeof x === "function";
}

/**
 * Enum service
 */
export default class EnumService extends BaseEnumService<Enum, SystemEnumTypes | SimpleListEnumTypes> {
  @observable protected enumCache: { [name: string]: { [key: string]: Enum | undefined } };

  constructor(protected repository: EnumRepository, protected eventBus: EventBus, private localeProvider: LocaleProvider) {
    super(repository);
    SystemEnums.forEach(enumsName => {
      const enumName = pluralize.singular(camelCase(enumsName));
      this.eventBus.subscribe(`${enumName}.*`, () => this.onEnumChange(enumsName));
    });
  }

  value(name: "charge_types", code: string | number): ChargeType | undefined;
  value(name: "fuel_types", code: string | number): FuelTypeResponse | undefined;
  value(name: "exemption_reasons", code: string | number): ExemptionReasonResponse | undefined;
  value(name: "payment_templates", code: string | number): PaymentTemplateSimpleListResponse | undefined;
  value(name: "paygate_refund_payment_templates", code: string | number): PaymentTemplateSimpleListResponse | undefined;
  value(name: "transfer_refund_payment_templates", code: string | number): PaymentTemplateSimpleListResponse | undefined;
  value(name: "inventory_accounts", code: string | number): InventoryAccountsResponse | undefined;
  value(name: "municipalities", code: string | number): MunicipalitySimpleListResponse | undefined;
  value(name: "distributors", code: string | number): DistributorSimpleListResponse | undefined;
  value(name: "regions", code: string | number): RegionSimpleListResponse | undefined;
  value(name: "roads", code: string | number): RoadsSimpleListResponse | undefined;
  value(
    name: "refund_for_refund_of_refund_payment_templates",
    code: string | number
  ): PaymentTemplateSimpleListResponse | undefined;
  value(
    name: "paygate_to_transfer_refund_payment_templates",
    code: string | number
  ): PaymentTemplateSimpleListResponse | undefined;
  value(name: "countries", code: string | number): CountryResponse | undefined;
  value(name: SystemEnumTypes, code: string | number): Enum | undefined;
  value(name: SystemEnumTypes | SimpleListEnumTypes, code: string | number) {
    const values = super.value(name, code);

    if (name === "exemption_reasons") {
      return (values as unknown as ExemptionReasonResponse[]).filter(
        item => item.languageCode.toLowerCase() === this.localeProvider.locale
      );
    }

    if (name === "municipalities") {
      return values as unknown as MunicipalitySimpleListResponse;
    }

    if (name === "distributors") {
      return values as unknown as DistributorSimpleListResponse;
    }

    if (name === "notifiable_exemption_reasons") {
      return values as unknown as NotifiableExemptionReasonSimpleList;
    }

    if (name === "regions") {
      return values as unknown as RegionSimpleListResponse;
    }

    if (name === "roads") {
      return values as unknown as RoadsSimpleListResponse;
    }

    return values;
  }

  async onEnumChange(name: SystemEnumTypes) {
    const changedEnum = await this.repository.getSystemEnum(name);

    this.enums = {
      ...this.enums,
      [name]: changedEnum,
    };
    this.enumCache[name] = {};
  }

  protected valueCompareFunction(name: SystemEnumTypes, code: string | number) {
    if (name === "exemption_reasons") {
      return (item: Enum) => (item as ExemptionReasonResponse).exemptionReason === code;
    }

    return (item: Enum) => item.id === code;
  }

  enumProxy = {
    get: (target: any, prop: any, receiver: any): any => {
      const enumAttribute = getEnumAttribute(target, prop);

      if (enumAttribute) {
        const enumId = target[enumAttribute.sourceAttribute];
        const enumName = enumAttribute.enumName;
        return this.value(enumName, enumId);
      } else {
        return target[prop];
      }
    },
  };

  values(name: "charge_types"): ChargeType[];
  values(name: "fuel_types"): FuelTypeResponse[];
  values(name: "payment_templates"): PaymentTemplateSimpleListResponse[];
  values(name: "paygate_refund_payment_templates"): PaymentTemplateSimpleListResponse[];
  values(name: "transfer_refund_payment_templates"): PaymentTemplateSimpleListResponse[];
  values(name: "paygate_to_transfer_refund_payment_templates"): PaymentTemplateSimpleListResponse[];
  values(name: "refund_for_refund_of_refund_payment_templates"): PaymentTemplateSimpleListResponse[];
  values(name: "inventory_accounts"): InventoryAccountsResponse[];
  values(name: "municipalities"): MunicipalitySimpleListResponse[];
  values(name: "distributors"): DistributorSimpleListResponse[];
  values(name: "regions"): RegionSimpleListResponse[];
  values(name: "roads"): RoadsSimpleListResponse[];
  values(name: "exemption_reasons"): ExemptionReasonResponse[];
  values(name: "notifiable_exemption_reasons"): NotifiableExemptionReasonSimpleList[];
  values(name: "countries"): CountryResponse[];
  values(name: SystemEnumTypes): Enum[];
  values(name: SystemEnumTypes | SimpleListEnumTypes) {
    const values = super.values(name);

    if (name === "exemption_reasons") {
      return (values as ExemptionReasonResponse[]).filter(item => item.languageCode.toLowerCase() === this.localeProvider.locale);
    }

    if (name === "municipalities") {
      return values as unknown as MunicipalitySimpleListResponse[];
    }

    if (name === "distributors") {
      return values as unknown as DistributorSimpleListResponse[];
    }

    if (name === "notifiable_exemption_reasons") {
      return values as unknown as NotifiableExemptionReasonSimpleList[];
    }

    if (name === "regions") {
      return values as unknown as RegionSimpleListResponse[];
    }

    if (name === "roads") {
      return values as unknown as RoadsSimpleListResponse[];
    }

    return values;
  }

  proxyObjectEnums(item: any) {
    const enumAttributes = getEnumAttributes(item);

    for (const attribute in item) {
      if (item.hasOwnProperty(attribute) && IsObject(item[attribute])) {
        item[attribute] = this.proxyObjectEnums(item[attribute]);
      }
    }

    if (enumAttributes) {
      return new Proxy(item, this.enumProxy);
    } else {
      return item;
    }
  }
}
